import {
	Dialog,
	FormattedText
} from "client/Components";
import { useDataInputDialog, useUserTimezone } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef
} from "react";
import { LinkAudienceKind, LinkPermission } from "./common";
import { SharedLink, SharedLinkHandle } from "./SharedLink";
import { TimeZone } from "client/DateAndTime/timezones";

type CopyLinkDialogProps = {
	documentName: string;
	audience: LinkAudienceKind;
	permission: LinkPermission;
	url: string;
	expirationDate?: string | Date;
	onClose: () => void;
};

const CopyLinkDialog = (props: CopyLinkDialogProps): ReactElement => {
	const {
		documentName,
		audience,
		permission,
		url,
		expirationDate,
		onClose
	} = props;

	const userTimezone: TimeZone = useUserTimezone();
	const linkRef: RefObject<SharedLinkHandle | null> = useRef<SharedLinkHandle>(null);

	useEffect(
		(): void => {
			linkRef.current
				?.copy();
		},
		[]
	);

	return (
		<Dialog trackingId="sp-share-link-copy-dialog">
			<Dialog.Header
				title={(
					<FormattedText
						text={localize("_ShareLinkForName")}
						thingsToReplace={[
							{ what: "{{Name}}", filler: documentName }
						]}
					/>
				)}
				onClose={onClose}
			/>

			<Dialog.Body>
				<SharedLink
					audience={audience}
					permission={permission}
					url={url}
					expirationDate={expirationDate}
					trackingId="link"
					ref={linkRef}
					timezone={userTimezone}
					buttonKind="primary"
				/>
			</Dialog.Body>

			<Dialog.Footer />
		</Dialog>
	);
};
CopyLinkDialog.displayName = "CopyLinkDialog";

export type CopyLinkDialogInitialData = Omit<
	CopyLinkDialogProps,
	"onClose"
>;

export function useCopyLinkDialog(): [
	element: ReactElement | null,
	open: (props: CopyLinkDialogInitialData) => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<CopyLinkDialogInitialData, void>(
		(props, onClose) => (
			<CopyLinkDialog
				{...props}
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (props: CopyLinkDialogInitialData): Promise<void> => {
			await originalOpen(props);
		},
		[originalOpen]
	);

	return [element, open];
}
